import {
  action$,
  derivedStream,
  persistentReducedStream,
  reducedStream,
  reducer,
  reduceState,
} from '@ardoq/rxbeach';
import { reducers } from './reducers';
import {
  ruleErrorToMessage,
  validateAdvancedSearchQuery,
  createFieldsLookupForValidation,
} from 'search/AdvancedSearch/utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { fields$ } from 'streams/fields/fields$';
import { defaultState } from './consts';
import { AdvancedSearchStateShape, AdvancedSearchShape } from './types';
import { APIFieldType, type APIFieldAttributes } from '@ardoq/api-types';
import type { StreamCollection } from 'streams/utils/streamUtils';
import { Locale } from '@ardoq/locale';
import { locale$ } from 'streams/locale$';
import type { I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { applicationStarted } from 'streams/context/ContextActions';
import { returnTrue } from '@ardoq/common-helpers';

export const advancedSearchState$ = action$.pipe(
  reduceState('advancedSearch$', defaultState, reducers)
);

export const createAdvancedSearchState$ = (
  namespace: string
): Observable<AdvancedSearchStateShape> =>
  reducedStream(`advancedSearch$:${namespace}`, defaultState, reducers, {
    namespace,
  });

const mapState = ([
  advancedSearchState,
  fieldsCollection,
  locale,
  i18nLocale,
  isApplicationStarted,
]: [
  AdvancedSearchStateShape,
  StreamCollection<APIFieldAttributes>,
  Locale,
  I18nLocale,
  boolean,
]): AdvancedSearchShape => {
  if (!isApplicationStarted) {
    return {
      ...defaultState,
      ruleErrorMessages: [],
      queryBuilderFields: [],
      numberFormatByFieldName: {},
      locale,
      i18nLocale,
    };
  }

  // There are two separate sets of fields:
  // Those used for building the query (all fields currently existing in the org), and
  // those fields that are relevant for the results (used for rendering the result table and column chooser)
  const queryBuilderFields = fieldsCollection.models;
  const resultFields = advancedSearchState.resultFields;

  const ruleErrors = advancedSearchState.queryBuilderRules
    ? validateAdvancedSearchQuery(advancedSearchState.queryBuilderRules)
    : [];

  const fieldLabelByName = createFieldsLookupForValidation(
    queryBuilderFields,
    i18nLocale
  );
  const ruleErrorMessages = ruleErrors.map(ruleError =>
    ruleErrorToMessage(ruleError, fieldLabelByName, i18nLocale)
  );

  // Resolve number formats up-front so the (non-permitted) cell renderer in
  // SearchResultTable receives plain data instead of calling fieldInterface.
  const numberFormatByFieldName = Object.fromEntries(
    resultFields
      .filter(field => field.type === APIFieldType.NUMBER)
      .map(field => [field.name, field.numberFormatOptions ?? null])
  );

  return {
    ...advancedSearchState,
    ruleErrorMessages,
    queryBuilderFields,
    numberFormatByFieldName,
    locale,
    i18nLocale,
  };
};

// Persistent so late subscribers (e.g. the lazily-mounted search UI) still see
// that the app has started; a plain action$ stream would miss the one-shot
// applicationStarted action fired during bootstrap.
const isApplicationStarted$ = persistentReducedStream(
  'advancedSearch-isApplicationStarted$',
  false,
  [reducer(applicationStarted, returnTrue)]
);

const advancedSearch$ = derivedStream(
  'advancedSearch-withFields$',
  advancedSearchState$,
  fields$,
  locale$,
  i18nLocale$,
  isApplicationStarted$
).pipe(map(mapState));

export const createAdvancedSearch$ = (
  namespace: string
): Observable<AdvancedSearchShape> =>
  derivedStream(
    `advancedSearch-withFields$:${namespace}`,
    createAdvancedSearchState$(namespace),
    fields$,
    locale$,
    i18nLocale$,
    isApplicationStarted$
  ).pipe(map(mapState));

export { advancedSearch$ };
